import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BarChart3, Building2, Music, Mic, TrendingUp 
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useTheme } from '../context/ThemeContext';
import { useBusiness } from '../context/BusinessContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { isLight } = useTheme();
  const { isSuperAdmin, selectedBusinessId: userBusinessId, businesses: contextBusinesses } = useBusiness();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('all');
  const [playbackData, setPlaybackData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set initial business ID based on user role
  useEffect(() => {
    if (!isSuperAdmin && userBusinessId) {
      // Business admins can only see their own business
      setSelectedBusinessId(userBusinessId);
    }
  }, [isSuperAdmin, userBusinessId]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchBusinesses();
    } else {
      // For business admins, use their assigned business
      setBusinesses(contextBusinesses.filter(b => b.id === userBusinessId));
    }
  }, [isSuperAdmin, contextBusinesses, userBusinessId]);

  useEffect(() => {
    fetchReportData();
    
    // Auto-refresh every 15 seconds
    const refreshInterval = setInterval(() => {
      fetchReportData();
    }, 15000);
    
    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessId, isSuperAdmin, userBusinessId]);

  const fetchBusinesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data);
    } catch (error) {
      toast.error('Failed to fetch businesses');
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // For business admins, always filter by their business
      let businessQuery = '';
      if (!isSuperAdmin && userBusinessId) {
        businessQuery = `&business_id=${userBusinessId}`;
      } else if (selectedBusinessId !== 'all') {
        businessQuery = `&business_id=${selectedBusinessId}`;
      }

      const playbackRes = await axios.get(`${API}/reports/playback-summary?days=10${businessQuery}`, { headers });
      setPlaybackData(playbackRes.data);
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  // Format chart data for display (show all 30 days)
  const formatChartData = () => {
    return playbackData.map(item => ({
      ...item,
      dateLabel: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }));
  };

  // Calculate totals
  const totalSongsMinutes = playbackData.reduce((sum, d) => sum + (d.songs_minutes || 0), 0);
  const totalAnnouncementsMinutes = playbackData.reduce((sum, d) => sum + (d.announcements_minutes || 0), 0);
  const totalSongsCount = playbackData.reduce((sum, d) => sum + (d.songs_count || 0), 0);
  const totalAnnouncementsCount = playbackData.reduce((sum, d) => sum + (d.announcements_count || 0), 0);

  // Format minutes to hours and minutes
  const formatMinutes = (mins) => {
    if (mins < 60) return `${Math.round(mins)}m`;
    const hours = Math.floor(mins / 60);
    const minutes = Math.round(mins % 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={isLight ? 'text-gray-500' : 'text-slate-400'}>Loading reports...</div>
      </div>
    );
  }

  return (
    <div className={`p-6 min-h-screen ${isLight ? 'bg-[#faf8f5]' : ''}`} data-testid="reports-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold mb-1 flex items-center gap-3 ${isLight ? 'text-gray-800' : 'text-white'}`}>
              <BarChart3 className={`w-7 h-7 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
              Reports & Analytics
            </h1>
            <p className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
              Playback statistics for the past 10 days
              {!isSuperAdmin && businesses.length > 0 && ` - ${businesses[0]?.name}`}
            </p>
          </div>
          {/* Business selector - only show for super admin */}
          {isSuperAdmin && (
            <Select value={selectedBusinessId} onValueChange={setSelectedBusinessId}>
              <SelectTrigger className={`w-48 ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}`} data-testid="business-filter">
                <SelectValue placeholder="All Businesses" />
              </SelectTrigger>
              <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}>
                <SelectItem value="all">All Businesses</SelectItem>
                {businesses.map((business) => (
                  <SelectItem key={business.id} value={business.id}>
                    {business.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Summary Stats - Minutes based */}
        <div className="grid grid-cols-3 gap-4">
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#8b9a7d]/10' : 'bg-emerald-600/20'}`}>
                <TrendingUp className={`w-5 h-5 ${isLight ? 'text-[#8b9a7d]' : 'text-emerald-400'}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>{formatMinutes(totalSongsMinutes + totalAnnouncementsMinutes)}</div>
                <div className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Total Playback (10d)</div>
              </div>
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#e94560]/10' : 'bg-violet-600/20'}`}>
                <Music className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>{formatMinutes(totalSongsMinutes)}</div>
                <div className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Total Songs (10d)</div>
              </div>
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#c4a35a]/10' : 'bg-amber-600/20'}`}>
                <Mic className={`w-5 h-5 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>{formatMinutes(totalAnnouncementsMinutes)}</div>
                <div className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Total Announcements (10d)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Playback Chart - Past 10 Days in Minutes */}
        <div className={`rounded-xl border p-5 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
            <BarChart3 className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
            Playback Duration (Last 10 Days) - Minutes
          </h2>
          
          {playbackData.length > 0 ? (
            <div className="h-80" data-testid="playback-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formatChartData()} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isLight ? '#ebe7e0' : '#334155'} />
                  <XAxis 
                    dataKey="dateLabel" 
                    stroke={isLight ? '#9ca3af' : '#64748b'}
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke={isLight ? '#9ca3af' : '#64748b'}
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: isLight ? '#9ca3af' : '#64748b' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isLight ? '#ffffff' : '#1e293b', 
                      border: isLight ? '1px solid #ebe7e0' : '1px solid #334155',
                      borderRadius: '8px',
                      color: isLight ? '#374151' : '#fff'
                    }}
                    labelStyle={{ color: isLight ? '#6b7280' : '#94a3b8' }}
                    formatter={(value, name, props) => {
                      const minutes = Math.round(value);
                      const dataKey = props?.dataKey;
                      if (dataKey === 'songs_minutes') {
                        return [`${minutes} min`, 'Songs'];
                      }
                      return [`${minutes} min`, 'Announcements'];
                    }}
                  />
                  <Legend />
                  <Bar 
                    dataKey="songs_minutes" 
                    name="Songs (min)" 
                    fill={isLight ? '#e94560' : '#8b5cf6'}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="announcements_minutes" 
                    name="Announcements (min)" 
                    fill={isLight ? '#c4a35a' : '#f59e0b'}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={`h-80 flex items-center justify-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No playback data available yet</p>
                <p className={`text-sm mt-1 ${isLight ? 'text-gray-300' : 'text-slate-600'}`}>Play some music to see analytics here</p>
              </div>
            </div>
          )}
        </div>

        {/* Business Breakdown */}
        {businesses.length > 1 && selectedBusinessId === 'all' && (
          <div className={`rounded-xl border p-5 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
              <Building2 className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
              Businesses Overview
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {businesses.map((business) => (
                <div 
                  key={business.id}
                  className={`rounded-lg border p-4 transition-colors cursor-pointer ${
                    isLight 
                      ? 'border-[#ebe7e0] bg-[#faf8f5] hover:border-[#e94560]/30' 
                      : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
                  }`}
                  onClick={() => setSelectedBusinessId(business.id)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {business.logo_url ? (
                      <img 
                        src={business.logo_url.startsWith('http') ? business.logo_url : `${BACKEND_URL}${business.logo_url}`}
                        alt={business.name}
                        className={`w-10 h-10 rounded-lg object-contain ${isLight ? 'bg-white' : 'bg-white/10'}`}
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#e94560]/10' : 'bg-rose-600/20'}`}>
                        <Building2 className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                      </div>
                    )}
                    <div>
                      <h3 className={`font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{business.name}</h3>
                      <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{business.field_of_business || 'Business'}</p>
                    </div>
                  </div>
                  <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Click to view details</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {playbackData.length === 0 && !loading && (
          <div className={`rounded-xl border p-12 text-center ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <BarChart3 className={`w-16 h-16 mx-auto mb-4 ${isLight ? 'text-gray-300' : 'text-slate-600'}`} />
            <p className={`text-lg ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>No report data available</p>
            <p className={`text-sm mt-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Play some music to see analytics here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
